import React, { createContext, useContext } from 'react';
import { BarChart2, MessageSquare, TrendingUp, Type } from 'react-feather';
import type { WidgetInstance } from '@trinity/chat';
import type { LibraryWidgetConfig } from '@trinity/chat/types/dashboard-config';
import type { WidgetMapContextValue } from '../../../containers/WidgetGrid/context.js';

export type LibraryCategory = 'chart' | 'kpi' | 'quote' | 'media';

export type PaletteEntry = {
  type: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ size?: number; color?: string; style?: React.CSSProperties }>;
  disabled?: boolean;
  fullWidth?: boolean;
  /** When set, Next opens the project widget library filtered to this kind instead of calling onSelect. */
  library?: LibraryCategory;
};

export type BuilderPaletteConfig = {
  entries: PaletteEntry[];
  /** The host's per-project widgets query; undefined while loading. */
  libraryWidgets: WidgetInstance[] | undefined;
  widgetComponentMap: WidgetMapContextValue;
  refetchLibraryWidgets?: () => void;
  /** Maps a picked library widget to the page-builder widget type + config that references it. */
  toPageWidget: (widget: WidgetInstance) => { type: string; config: LibraryWidgetConfig };
};

/** Tiles for library-backed builders (segment dashboard, PJ grid). */
export const LIBRARY_PALETTE_ENTRIES: PaletteEntry[] = [
  { type: 'chart', label: 'Charts', icon: BarChart2, library: 'chart' },
  { type: 'kpi', label: 'KPI Card', icon: TrendingUp, library: 'kpi' },
  { type: 'text', label: 'Rich Text', icon: Type },
  { type: 'quote', label: 'Quotes', icon: MessageSquare, library: 'quote' },
];

const BuilderPaletteContext = createContext<BuilderPaletteConfig | null>(null);

export function BuilderPaletteProvider({
  value,
  children,
}: {
  value: BuilderPaletteConfig;
  children: React.ReactNode;
}): React.ReactElement {
  return <BuilderPaletteContext.Provider value={value}>{children}</BuilderPaletteContext.Provider>;
}

/** Null when no host override is provided — the palette then shows its default tiles. */
export function useBuilderPalette(): BuilderPaletteConfig | null {
  return useContext(BuilderPaletteContext);
}
